import { Chess } from 'chess.js';

import {
  ChessJsRulesPort,
  type RulesColor,
  type RulesSnapshot,
  type VerboseLegalMove,
} from '../rules';
import type { GameClockState, PlayerMode, SessionResult } from '../session';

const UCI_PATTERN = /^[a-h][1-8][a-h][1-8][qrbn]?$/;
const PGN_MIME_TYPE = 'application/x-chess-pgn;charset=utf-8';

export interface GameReviewExportInput {
  readonly initialFen: string;
  readonly acceptedUciMoves: readonly string[];
  readonly gameId?: string;
  readonly createdAtMs?: number;
  readonly updatedAtMs?: number;
  readonly result?: SessionResult;
  readonly mode?: PlayerMode;
  readonly clock?: GameClockState;
}

export interface ReviewPly {
  /** One-based index compatible with GameSession's review-ply selection. */
  readonly ply: number;
  readonly moveNumber: number;
  readonly color: RulesColor;
  readonly uci: string;
  readonly san: string;
  readonly from: string;
  readonly to: string;
  readonly promotion?: string;
  readonly beforeFen: string;
  readonly afterFen: string;
}

export interface ReviewMoveRow {
  readonly moveNumber: number;
  readonly white?: ReviewPly;
  readonly black?: ReviewPly;
}

export interface PgnDownload {
  readonly filename: string;
  readonly content: string;
  readonly mimeType: typeof PGN_MIME_TYPE;
}

export interface GameReviewExport {
  readonly initialFen: string;
  readonly finalFen: string;
  readonly result: SessionResult;
  readonly plies: readonly ReviewPly[];
  readonly moveRows: readonly ReviewMoveRow[];
  readonly pgn: string;
  readonly download: PgnDownload;
}

export type GameReviewExportFailureCode =
  | 'invalid-input'
  | 'invalid-initial-fen'
  | 'invalid-uci'
  | 'illegal-uci';

export interface GameReviewExportFailure {
  readonly code: GameReviewExportFailureCode;
  readonly message: string;
  readonly eventIndex?: number;
  readonly uci?: unknown;
}

export type GameReviewExportResult =
  | { readonly ok: true; readonly value: GameReviewExport }
  | { readonly ok: false; readonly error: GameReviewExportFailure };

/**
 * Rebuilds review and PGN data solely from the persisted replay authority.
 * Stored SAN and derived FEN are deliberately not accepted by this boundary.
 */
export function createGameReviewExport(
  input: GameReviewExportInput,
): GameReviewExportResult {
  if (
    input === null ||
    typeof input !== 'object' ||
    typeof input.initialFen !== 'string' ||
    !Array.isArray(input.acceptedUciMoves)
  ) {
    return rejected({
      code: 'invalid-input',
      message: 'Review export requires an initial FEN and an ordered UCI move array.',
    });
  }

  let rules: ChessJsRulesPort;
  try {
    rules = new ChessJsRulesPort(input.initialFen);
  } catch {
    return rejected({
      code: 'invalid-initial-fen',
      message: 'The initial FEN is not a valid chess position.',
    });
  }

  const plies: ReviewPly[] = [];

  for (const [eventIndex, rawUci] of input.acceptedUciMoves.entries()) {
    if (typeof rawUci !== 'string' || !UCI_PATTERN.test(rawUci)) {
      return rejected({
        code: 'invalid-uci',
        message: `Replay event ${eventIndex + 1} is not lowercase UCI coordinate notation.`,
        eventIndex,
        uci: rawUci,
      });
    }

    const before = rules.snapshot();
    const attempt = rules.submit({
      expectedRevision: before.revision,
      from: rawUci.slice(0, 2),
      to: rawUci.slice(2, 4),
      ...(rawUci.length === 5 ? { promotion: rawUci[4] as 'q' | 'r' | 'b' | 'n' } : {}),
    });
    if (!attempt.accepted) {
      return rejected({
        code: 'illegal-uci',
        message: `Replay event ${eventIndex + 1} is illegal in its preceding position.`,
        eventIndex,
        uci: rawUci,
      });
    }

    plies.push(toReviewPly(attempt.value.verbose, eventIndex + 1));
  }

  const snapshot = rules.snapshot();
  const result = resolveResult(snapshot, input);
  const chess = createPgnSerializer(snapshot.initialFen, plies, snapshot.fen);
  setPgnHeaders(chess, input, result, plies.length);

  const rawPgn = chess.pgn({ newline: '\n', maxWidth: 80 });
  const pgn = plies.length === 0
    ? rawPgn.replace(/\n[ \t]*(?=(?:1-0|0-1|1\/2-1\/2|\*)$)/, '\n\n')
    : rawPgn;
  const moveRows = toMoveRows(plies);
  const download = Object.freeze({
    filename: toFilename(input.gameId),
    content: pgn,
    mimeType: PGN_MIME_TYPE,
  });

  const value: GameReviewExport = Object.freeze({
    initialFen: snapshot.initialFen,
    finalFen: snapshot.fen,
    result,
    plies: Object.freeze(plies),
    moveRows,
    pgn,
    download,
  });

  return Object.freeze({ ok: true, value });
}

function toReviewPly(move: VerboseLegalMove, ply: number): ReviewPly {
  const moveNumber = Number(move.before.split(/\s+/)[5]);
  return Object.freeze({
    ply,
    moveNumber,
    color: move.color,
    uci: `${move.from}${move.to}${move.promotion ?? ''}`,
    san: move.san,
    from: move.from,
    to: move.to,
    ...(move.promotion ? { promotion: move.promotion } : {}),
    beforeFen: move.before,
    afterFen: move.after,
  });
}

function toMoveRows(plies: readonly ReviewPly[]): readonly ReviewMoveRow[] {
  const rows: ReviewMoveRow[] = [];

  for (const ply of plies) {
    const previous = rows.at(-1);
    if (previous?.moveNumber === ply.moveNumber) {
      rows[rows.length - 1] = Object.freeze({
        ...previous,
        ...(ply.color === 'w' ? { white: ply } : { black: ply }),
      });
      continue;
    }

    rows.push(Object.freeze({
      moveNumber: ply.moveNumber,
      ...(ply.color === 'w' ? { white: ply } : { black: ply }),
    }));
  }

  return Object.freeze(rows);
}

function resolveResult(
  snapshot: RulesSnapshot,
  input: GameReviewExportInput,
): SessionResult {
  const canonical = snapshot.result;
  if (canonical.terminal) {
    return canonical;
  }

  if (isTimeoutResult(input.result)) {
    return Object.freeze({ ...input.result });
  }

  if (isResignationResult(input.result)) {
    return Object.freeze({ ...input.result });
  }

  if (isProvenAgreementDraw(snapshot, input)) {
    return Object.freeze({
      terminal: true,
      token: '1/2-1/2',
      reason: 'draw-agreement',
    });
  }

  if (isProvenFlagFallDraw(snapshot, input)) {
    return Object.freeze({
      terminal: true,
      token: '1/2-1/2',
      reason: 'draw',
    });
  }

  return canonical;
}

function isProvenAgreementDraw(
  snapshot: RulesSnapshot,
  input: GameReviewExportInput,
): boolean {
  const result = input.result;
  return (
    result?.terminal === true &&
    result.token === '1/2-1/2' &&
    result.reason === 'draw-agreement' &&
    input.mode?.kind === 'hot-seat' &&
    !snapshot.result.terminal &&
    (input.clock === undefined ||
      (Number.isSafeInteger(input.updatedAtMs) &&
        input.updatedAtMs !== undefined &&
        input.clock.activeDeadlineMs > input.updatedAtMs))
  );
}

/**
 * `draw` is also RulesPort's generic canonical fallback, so the reason alone
 * cannot identify a session clock event. Accept the overlay only when the
 * persisted deadline and the value-only material projection independently
 * reproduce GameSession's narrow bare-king flag-fall adjudication.
 */
function isProvenFlagFallDraw(
  snapshot: RulesSnapshot,
  input: GameReviewExportInput,
): boolean {
  const result = input.result;
  const clock = input.clock;
  return (
    result?.terminal === true &&
    result.token === '1/2-1/2' &&
    result.reason === 'draw' &&
    clock !== undefined &&
    (clock.activeColor === 'w' || clock.activeColor === 'b') &&
    clock.activeColor === snapshot.turn &&
    Number.isSafeInteger(clock.activeDeadlineMs) &&
    clock.activeDeadlineMs >= 0 &&
    Number.isSafeInteger(input.updatedAtMs) &&
    input.updatedAtMs !== undefined &&
    input.updatedAtMs >= clock.activeDeadlineMs &&
    snapshot.nonKingPieceCount[opposite(clock.activeColor)] === 0
  );
}

function opposite(color: RulesColor): RulesColor {
  return color === 'w' ? 'b' : 'w';
}

/**
 * chess.js remains private here only because it owns PGN formatting. Every
 * move was already accepted by RulesPort, and these assertions prevent this
 * serializer projection from silently drifting from the authoritative replay.
 */
function createPgnSerializer(
  initialFen: string,
  plies: readonly ReviewPly[],
  expectedFinalFen: string,
): Chess {
  const chess = new Chess(initialFen);

  for (const ply of plies) {
    const serializedMove = chess.move({
      from: ply.from,
      to: ply.to,
      ...(ply.promotion ? { promotion: ply.promotion } : {}),
    });

    if (
      `${serializedMove.from}${serializedMove.to}${serializedMove.promotion ?? ''}` !== ply.uci ||
      serializedMove.san !== ply.san ||
      serializedMove.before !== ply.beforeFen ||
      serializedMove.after !== ply.afterFen
    ) {
      throw new Error(`PGN serializer diverged from RulesPort at ply ${ply.ply}.`);
    }
  }

  if (chess.fen() !== expectedFinalFen) {
    throw new Error('PGN serializer final FEN diverged from RulesPort.');
  }

  return chess;
}

function isTimeoutResult(result: SessionResult | undefined): result is Extract<SessionResult, { reason: 'timeout' }> {
  return (
    result?.terminal === true &&
    result.reason === 'timeout' &&
    ((result.token === '1-0' && result.winner === 'w') ||
      (result.token === '0-1' && result.winner === 'b'))
  );
}

function isResignationResult(
  result: SessionResult | undefined,
): result is Extract<SessionResult, { reason: 'resignation' }> {
  return (
    result?.terminal === true &&
    result.reason === 'resignation' &&
    ((result.token === '1-0' && result.winner === 'w') ||
      (result.token === '0-1' && result.winner === 'b'))
  );
}

function setPgnHeaders(
  chess: Chess,
  input: GameReviewExportInput,
  result: SessionResult,
  plyCount: number,
): void {
  const players = toPlayerHeaders(input.mode);
  chess.setHeader('Event', 'CapyChess Friendly Game');
  chess.setHeader('Site', 'CapyChess Offline');
  chess.setHeader('Date', toPgnDate(input.createdAtMs));
  chess.setHeader('Round', '-');
  chess.setHeader('White', players.white);
  chess.setHeader('Black', players.black);
  chess.setHeader('Result', result.token);
  chess.setHeader('Termination', toTermination(result));
  chess.setHeader('PlyCount', String(plyCount));
  chess.setHeader('CapyChessStatus', result.reason);

  if (typeof input.gameId === 'string' && input.gameId.length > 0) {
    chess.setHeader('CapyChessGameId', toPgnString(input.gameId));
  }

  if (input.mode?.kind === 'hot-seat') {
    chess.setHeader('CapyChessMode', 'Hot seat');
  } else if (input.mode?.kind === 'computer') {
    chess.setHeader('CapyChessMode', 'Computer');
  }
}

function toPlayerHeaders(mode: PlayerMode | undefined): { readonly white: string; readonly black: string } {
  if (mode?.kind !== 'computer') {
    return { white: 'CapyChess White', black: 'CapyChess Black' };
  }

  const capy = `CapyChess ${toPgnString(mode.ai.id)}`;
  return mode.humanColor === 'w'
    ? { white: 'Player', black: capy }
    : { white: capy, black: 'Player' };
}

function toTermination(result: SessionResult): string {
  if (!result.terminal) {
    return 'unterminated';
  }
  if (result.reason === 'timeout') {
    return 'time forfeit';
  }
  return result.reason === 'resignation' ? 'resignation' : 'normal';
}

function toPgnDate(timestamp: number | undefined): string {
  if (
    timestamp === undefined ||
    !Number.isSafeInteger(timestamp) ||
    timestamp < 0 ||
    timestamp > 8_640_000_000_000_000
  ) {
    return '????.??.??';
  }

  const date = new Date(timestamp);
  const year = String(date.getUTCFullYear()).padStart(4, '0');
  const month = String(date.getUTCMonth() + 1).padStart(2, '0');
  const day = String(date.getUTCDate()).padStart(2, '0');
  return `${year}.${month}.${day}`;
}

/** Prevents external metadata from breaking a PGN tag-pair string. */
function toPgnString(value: string): string {
  return value
    .replace(/\\/g, '\\\\')
    .replace(/"/g, '\\"')
    .replace(/[\r\n\t]+/g, ' ')
    .trim();
}

function toFilename(gameId: string | undefined): string {
  if (typeof gameId !== 'string') {
    return 'capychess-game.pgn';
  }

  const slug = gameId
    .normalize('NFKD')
    .toLowerCase()
    .replace(/[^a-z0-9._-]+/g, '-')
    .replace(/^[._-]+|[._-]+$/g, '')
    .slice(0, 80);
  return slug.length > 0 ? `capychess-${slug}.pgn` : 'capychess-game.pgn';
}

function rejected(error: GameReviewExportFailure): GameReviewExportResult {
  return Object.freeze({ ok: false, error: Object.freeze(error) });
}
